/**
 * PWA-UI-Komponenten:
 *   - OfflineBanner  – zeigt Warnung bei fehlender Verbindung
 *   - UpdateBanner   – zeigt „Neue Version verfügbar“ + Neu laden
 *   - InstallPromptButton / InstallHintDialog – Installations-Prompt
 */
import React from "react";
import { WifiOff, RefreshCw, Download, Info, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { usePwa } from "@/context/PwaContext";

export const OfflineBanner = () => {
  const { online } = usePwa();
  if (online) return null;
  return (
    <div
      data-testid="pwa-offline-banner"
      className="sticky top-0 z-40 flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-1.5 text-xs text-amber-900"
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 leading-tight">
        <strong>Offline</strong> – neue Daten können erst nach Wiederherstellung der
        Verbindung geladen werden. Bereits geladene Seiten stehen weiterhin zur Verfügung.
      </span>
    </div>
  );
};

export const UpdateBanner = () => {
  const { updateReady, applyUpdate } = usePwa();
  if (!updateReady) return null;
  return (
    <div
      data-testid="pwa-update-banner"
      className="sticky top-0 z-40 flex items-center gap-2 border-b border-emerald-300 bg-emerald-50 px-4 py-1.5 text-xs text-emerald-900"
    >
      <RefreshCw className="h-3.5 w-3.5 shrink-0" />
      <span className="flex-1 leading-tight">
        <strong>Neue Version verfügbar.</strong> Zum Anwenden bitte neu laden.
      </span>
      <Button
        data-testid="pwa-update-apply"
        size="sm"
        variant="outline"
        className="h-6 border-emerald-400 text-emerald-900 hover:bg-emerald-100"
        onClick={applyUpdate}
      >
        Aktualisieren
      </Button>
    </div>
  );
};

const LS_KEY = "pwa_install_dismissed_until";
const HIDE_MS = 7 * 24 * 60 * 60 * 1000; // 7 Tage

const isDismissed = () => {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (!v) return false;
    return Number(v) > Date.now();
  } catch { return false; }
};

const dismiss = () => {
  try {
    localStorage.setItem(LS_KEY, String(Date.now() + HIDE_MS));
  } catch { /* storage nicht verfügbar */ }
};

export const InstallPromptCard = () => {
  const { canInstall, installed, promptInstall, platform } = usePwa();
  const [hidden, setHidden] = React.useState(isDismissed());
  if (installed || !canInstall || hidden) return null;
  return (
    <div
      data-testid="pwa-install-card"
      className="relative rounded-lg border border-primary/20 bg-primary/[0.04] p-3 text-sm text-foreground"
    >
      <button
        aria-label="Ausblenden"
        onClick={() => { dismiss(); setHidden(true); }}
        className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-black/5"
        data-testid="pwa-install-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <Smartphone className="mt-0.5 h-4 w-4 text-primary" />
        <div className="flex-1">
          <p className="font-medium">seifried.app als App installieren</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Schneller Start vom Home-Bildschirm, ohne Browser-Leiste. Funktioniert
            auch bei kurzzeitigen Verbindungsproblemen (App-Shell offline).
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={promptInstall}
              data-testid="pwa-install-btn"
            >
              <Download className="mr-2 h-3.5 w-3.5" />
              {platform === "ios" ? "Anleitung anzeigen" : "Installieren"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const IosInstallHintDialog = () => {
  const { showIosHint, setShowIosHint } = usePwa();
  return (
    <Dialog open={showIosHint} onOpenChange={(o) => !o && setShowIosHint(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            App auf iOS/iPadOS installieren
          </DialogTitle>
          <DialogDescription>
            Safari erlaubt keine automatische Installation. In wenigen Schritten
            fügen Sie die App zum Home-Bildschirm hinzu:
          </DialogDescription>
        </DialogHeader>
        <ol className="ml-5 list-decimal space-y-1.5 text-sm text-foreground">
          <li>Tippen Sie unten in Safari auf das <strong>Teilen</strong>-Symbol.</li>
          <li>Wählen Sie <strong>„Zum Home-Bildschirm“</strong>.</li>
          <li>
            Bestätigen Sie mit <strong>„Hinzufügen“</strong>. Anschließend startet
            seifried.app wie eine native App.
          </li>
        </ol>
        <div className="mt-3 rounded-md bg-secondary/60 p-3 text-xs text-muted-foreground">
          <Info className="mr-1 inline h-3.5 w-3.5" />
          Nur in Safari verfügbar. Chrome/Firefox auf iOS unterstützen dies aktuell nicht.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowIosHint(false)}>
            Verstanden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
